/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2024 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/
import { useEffect, useState } from "react";

import * as React from "react";
import { Redirect } from "react-router-dom";
import {
  CmsApi,
  CmsImageAttributes,
  GuidePage as GuidePageType,
} from "api/CmsApi";
import { AppPath } from "components/AppPath";
import AppLoading from "components/AppLoading";
import { AppHeader, AppHeaderHeight } from "components/shared/AppHeader";
import { BlocksRenderer } from "@strapi/blocks-react-renderer";
import { SplashBackdrop } from "components/shared/Splash";
import styled from "styled-components";
import { devices } from "components/shared/Styles";
import {
  GuidePageH1,
  GuidePageH2,
  GuidePageH3,
  GuidePageH4,
  GuidePageH5,
  GuidePageOl,
  GuidePageParagraph,
  GuidePageUl,
  GuidePageImageBlock,
  GuidePageCodeBlock,
  IMAGE_COLUMN_WIDTH,
  textWidth,
} from "./GuidePageBlocks";
import backIconDark from "images/back-icon-dark.svg";
import { useInlineHydration } from "hooks/useInlineHydration";
import { AppFooter } from "components/shared/AppFooter";
import { getUserLocale } from "utilities/UserSettingsUtilities";
import { Helmet } from "react-helmet";
import { useGenerateTemplatedUrl } from "hooks/useTemplatedUrl";

const GUIDES_INDEX_PATH = "/guides";

const BackToIndexContainer = styled.a`
  display: flex;
  text-decoration: inherit;
  align-items: center;
  gap: 8px;
  margin-top: 64px;
  margin-left: auto;
  margin-right: auto;
  ${textWidth}

  ${devices.small} {
    margin-top: 32px;
  }
`;

const BackButtonIcon = styled.div`
  background-color: transparent;
  border: none;
  padding: 0;
  height: 22px;
  width: 22px;
  cursor: pointer;
  background-image: url("${backIconDark}");
  background-repeat: no-repeat;
  background-position: center;
`;

const BackButtonText = styled.span`
  font-size: 16px;
  font-weight: 400;
  color: var(--spectrum-black);
`;

const BackToIndex: React.FC = () => {
  return (
    <BackToIndexContainer href={GUIDES_INDEX_PATH}>
      <BackButtonIcon />
      <BackButtonText>{I18n.t("guides.all_guides")}</BackButtonText>
    </BackToIndexContainer>
  );
};

export const GuidePage: React.FC<{ slug: string }> = ({ slug }) => {
  const [guide, setGuide, wasHydrated] = useInlineHydration<
    GuidePageType | undefined
  >(`guide-page-${slug}`, undefined);
  const [guideLoaded, setGuideLoaded] = useState(false);
  const currentLocale = getUserLocale()!;

  useEffect(() => {
    let isMounted = true;
    CmsApi.instance
      .getGuidePage(slug)
      .then((fetchedGuidePage) => {
        if (!isMounted) return;
        if (fetchedGuidePage) {
          const isSnapshot = navigator.userAgent === "ReactSnap";
          // until we introduce more language specific urls, the snapshotter will
          // effectively be browsing in English, so we need to avoid changing the non-English
          // guide pages to English for the snapshot, which would cause a flicker upon
          // page load
          if (
            !isSnapshot &&
            fetchedGuidePage.attributes.locale !== currentLocale
          ) {
            const translations =
              fetchedGuidePage.attributes.localizations?.data ?? [];
            const localizedGuidePage =
              translations.find(
                (loc: any) => loc.attributes.locale === currentLocale
              ) ?? fetchedGuidePage;

            if (localizedGuidePage.attributes.slug !== slug) {
              return window.location.replace(
                `/guides/${localizedGuidePage.attributes.slug}`
              );
            }
          }
          setGuide(fetchedGuidePage);
        }
      })
      .catch((error) => {
        newrelic.noticeError(error);
      })
      .finally(() => {
        if (isMounted) {
          setGuideLoaded(true);
        }
      });
    return (): void => {
      isMounted = false;
    };
  }, [slug, currentLocale, setGuide]);

  if (!guideLoaded && !wasHydrated) {
    return <AppLoading />;
  }

  if (!guide) {
    return <Redirect to={GUIDES_INDEX_PATH} />;
  }

  return (
    <div
      style={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}
    >
      <SEOTags guide={guide} />
      <AppHeader />
      <GuidePageContainer>
        <GuidePageSplashBackdrop />
        <GuidePageColumn>
          <BackToIndex />
          <GuidePageTitle>{guide.attributes.Title}</GuidePageTitle>
          <GuidePageH2>{guide.attributes.Subtitle}</GuidePageH2>

          <GuidePageHeroImage
            image={guide.attributes.Hero.data.attributes}
            highFetchPriority
          />

          <BlocksRenderer
            content={guide.attributes.Content}
            blocks={{
              image: ({ image }): ReturnType<React.FC> => {
                return (
                  <GuidePageImageBlock image={image as CmsImageAttributes} />
                );
              },
              heading: ({ level, children }): ReturnType<React.FC> => {
                switch (level) {
                  case 1:
                    return <GuidePageH1>{children}</GuidePageH1>;
                  case 2:
                    return <GuidePageH2>{children}</GuidePageH2>;
                  case 3:
                    return <GuidePageH3>{children}</GuidePageH3>;
                  case 4:
                    return <GuidePageH4>{children}</GuidePageH4>;
                  case 5:
                  case 6:
                    return <GuidePageH5>{children}</GuidePageH5>;
                }
              },
              paragraph: ({ children }): ReturnType<React.FC> => {
                return <GuidePageParagraph>{children}</GuidePageParagraph>;
              },
              list: ({ children, format }): ReturnType<React.FC> => {
                switch (format) {
                  case "unordered":
                    return <GuidePageUl>{children}</GuidePageUl>;
                  case "ordered":
                    return <GuidePageOl>{children}</GuidePageOl>;
                }
              },
              code: (codeAttributes): ReturnType<React.FC> => {
                return (
                  <GuidePageCodeBlock codeJson={codeAttributes.plainText} />
                );
              },
            }}
            modifiers={{
              bold: ({ children }): ReturnType<React.FC> => (
                <span style={{ fontWeight: 800 }}>{children}</span>
              ),
            }}
          />
        </GuidePageColumn>
      </GuidePageContainer>
      <AppFooter />
    </div>
  );
};

const SEOTags: React.FC<{ guide: GuidePageType }> = ({ guide }) => {
  const generateUrl = useGenerateTemplatedUrl();

  return (
    <Helmet>
      <link
        rel="canonical"
        href={generateUrl(AppPath.GUIDES, {
          guidePageSlug: guide.attributes.slug,
        })}
      />
      <link
        rel="alternate"
        hrefLang={guide.attributes.locale}
        href={generateUrl(AppPath.GUIDES, {
          guidePageSlug: guide.attributes.slug,
        })}
      />
      {guide.attributes.localizations.data.map((localization) => {
        return (
          <link
            key={localization.id}
            rel="alternate"
            hrefLang={localization.attributes.locale}
            href={generateUrl(AppPath.GUIDES, {
              guidePageSlug: localization.attributes.slug,
            })}
          />
        );
      })}
    </Helmet>
  );
};

const GuidePageSplashBackdrop = styled(SplashBackdrop)`
  min-height: 450px;
  height: 450px;
  position: absolute;
  z-index: -1;
`;

const GuidePageColumn = styled.div`
  font-family: adobe-clean, sans-serif;
  color: var(--spectrum-gray-900);
  text-align: left;
  width: ${IMAGE_COLUMN_WIDTH}px;
  margin-bottom: 112px;

  & > * {
    margin-left: auto;
    margin-right: auto;
  }

  a,
  a:visited {
    color: var(--spectrum-indigo-900);
  }
`;

const GuidePageHeroImage = styled(GuidePageImageBlock)`
  ${devices.small} {
    margin-top: 32px;
  }
`;

const GuidePageContainer = styled.div`
  margin-top: ${AppHeaderHeight};
  flex-grow: 1;
  isolation: isolate;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const GuidePageTitle = styled(GuidePageH1)`
  background: linear-gradient(133.13deg, #5266ff 5.68%, #b871ff 96.33%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-top: 16px;
`;
